import React, { FC, useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@benchlabs/react-design-system';
import { CaretDownIcon } from '@benchlabs/react-design-system/dist/icons/phosphor/CaretDownIcon';
import { usePlaidLink } from 'react-plaid-link';
import usePlaidLinkToken from './usePlaidLinkToken';
import { callExchangePlaidToken } from './api';
import { LedgerAccount, UploadedFrom } from './types';
import { LedgerSelectSurvey } from './LedgerSelectSurvey';
import { LedgerSelectBpaUpload } from './LedgerSelectBpaUpload';
import env from 'bench-environment';

interface PlaidOcrUploadSectionProps {
  ledgerAccounts?: LedgerAccount[];
  businessId: string;
  uploadedFrom: UploadedFrom;
}

const PlaidOcrUploadSection: FC<PlaidOcrUploadSectionProps> = ({
  ledgerAccounts = [],
  businessId,
  uploadedFrom,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedLedgerId, setSelectedLedgerId] = useState<string>();
  const selectedLedgerIdRef = useRef<string | undefined>(undefined);
  const [selectedSurveyOption, setSelectedSurveyOption] = useState<string>();
  const [isSurveySubmitted, setIsSurveySubmitted] = useState<boolean>(false);
  const [otherReason, setOtherReason] = useState<string>();
  const preselectionReasonRef = useRef<string | undefined>(undefined);

  selectedLedgerIdRef.current = selectedLedgerId;
  preselectionReasonRef.current = otherReason ? otherReason.trim() : selectedSurveyOption;

  const defaultErrorMessage =
    'There was a problem uploading your document. Please try again and contact Application Support if issue persists.';

  const apiBasePath = env.location.apiRoot + '/au/financial-data-ingestion/plaid-token/api';

  const {
    token,
    hasError,
    isLoading: plaidIsLoading,
    refetchToken,
  } = usePlaidLinkToken(apiBasePath, businessId);

  const exchangePlaidToken = useCallback(
    (publicToken: string) => {
      setIsLoading(true);
      setErrorMessage('');

      callExchangePlaidToken(apiBasePath, businessId, publicToken, {
        ledgerId: selectedLedgerIdRef.current,
        uploadedFrom,
        preselectionReason: preselectionReasonRef.current,
      })
        .then(response => {
          if (!response.ok) {
            if (response.status === 401) {
              setErrorMessage('Your session may have expired. Please log in and try again.');
            } else {
              setErrorMessage(defaultErrorMessage);
            }
          }
        })
        .catch(() => {
          setErrorMessage(defaultErrorMessage);
        })
        .finally(() => {
          setIsLoading(false);
          setSelectedLedgerId(undefined);
          selectedLedgerIdRef.current = undefined;
          preselectionReasonRef.current = undefined;
          refetchToken();
        });
    },
    [apiBasePath, businessId, defaultErrorMessage, refetchToken, uploadedFrom],
  );

  const onSuccess = useCallback(
    (publicToken: string) => {
      exchangePlaidToken(publicToken);
      setIsSurveySubmitted(false);
      setSelectedSurveyOption(undefined);
      setOtherReason(undefined);
    },
    [exchangePlaidToken],
  );

  const { open, ready } = usePlaidLink({
    token,
    onSuccess,
    onExit: (err, metadata) => {
      setIsSurveySubmitted(false);
      setSelectedSurveyOption(undefined);
      setOtherReason(undefined);
      if (err) console.error('Plaid error: ', err); // eslint-disable-line no-console
      if (metadata) console.error(metadata); // eslint-disable-line no-console
    },
  });

  const onSurveySubmit = (
    isSurveySubmitted: boolean,
    selectedSurveyOption: string,
    otherReason?: string,
  ) => {
    setIsSurveySubmitted(isSurveySubmitted);
    setSelectedSurveyOption(selectedSurveyOption);
    setOtherReason(otherReason);
  };

  const handleLedgerAccountSelect = (ledgerId: string) => {
    setSelectedLedgerId(ledgerId);
  };

  if (isLoading || plaidIsLoading) {
    return <Typography component="p">Loading, do not close this page</Typography>;
  }

  if (hasError || !token) {
    return (
      <Alert severity="error">
        <Typography component="p" fontWeight="bold">
          Something went wrong
        </Typography>
        <Typography component="p">
          Please refresh the page and try again. Contact Application Support if issue persists.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box component="section">
      {errorMessage !== '' && (
        <Alert severity="error">
          <Typography component="p" fontWeight="bold">
            Something went wrong
          </Typography>
          <Typography component="p">{errorMessage}</Typography>
        </Alert>
      )}
      <Box>
        <Typography variant="h2">Instructions</Typography>
        <Typography component="div" sx={{ p: '10px 30px' }}>
          <ol style={{ listStyleType: 'decimal' }}>
            <li>Click the &quot;Choose Files&quot; button below</li>
            <li>Choose any type of bank statements, with a limit of 12</li>
            <li>Any errors will be rerouted back for manual DP</li>
          </ol>
        </Typography>
      </Box>
      <Box mt={3}>
        <Accordion>
          <AccordionSummary
            aria-controls="ledger-select-accordion-content"
            expandIcon={<CaretDownIcon />}
            id="ledger-select-accordion-header"
          >
            <Typography component="h3" flexGrow={1} variant="h3" color="primary">
              OR Select a Ledger Account
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <LedgerSelectSurvey onSurveySubmit={onSurveySubmit} />
            {isSurveySubmitted &&
              (ledgerAccounts.length > 0 ? (
                <Box mt={3}>
                  <LedgerSelectBpaUpload
                    ledgerAccounts={ledgerAccounts}
                    onLedgerAccountSelect={handleLedgerAccountSelect}
                  />
                </Box>
              ) : (
                <Typography component="p">No ledger accounts found.</Typography>
              ))}
          </AccordionDetails>
        </Accordion>
      </Box>
      <Box mt={3}>
        <Button disabled={!ready} onClick={() => open()} variant="contained">
          Choose Files
        </Button>
      </Box>
    </Box>
  );
};

export default PlaidOcrUploadSection;
